import { useEffect, useMemo, useState } from "react";
import api, { API_BASE, normalizeDecimal, DECIMAL_HINT, convertIngredientToItemBase, itemCostPerBase, itemBase } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { AlertTriangle, Plus, Trash2, ChefHat, Pencil, Copy, Scaling, FileUp, CheckSquare, Square, Upload, Beaker, Download, FileText, Weight, Package as PackageIcon } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import ImportDialog from "../components/ImportDialog";
import ItemCombobox from "../components/ItemCombobox";

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({ name: "", output_item_id: "", output_quantity: 1, output_unit: "g", notes: "", scale_ings: true });
  const [ings, setIngs] = useState([{ item_id: "", quantity: "", unit: "kg" }]);
  const [detail, setDetail] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [scaleDlg, setScaleDlg] = useState(null);   // { recipe, factor, name }
  const [selected, setSelected] = useState({});
  const confirm = useConfirm();

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const allOn = recipes.length > 0 && recipes.every((r) => selected[r.id]);
    const next = {};
    if (!allOn) recipes.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    const ok = await confirm({
      title: `${selectedIds.length} Rezeptur/-en löschen?`,
      description:
        "Die ausgewählten Rezepturen werden endgültig entfernt. Bereits produzierte Chargen und Lagerbestände bleiben unverändert – Produktionsaufträge behalten ihre Daten.",
      confirmLabel: `${selectedIds.length}× löschen`,
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/recipes/bulk-delete", { ids: selectedIds });
      toast.success(`${data.deleted}/${data.total} Rezeptur/-en gelöscht`);
      if (detail && selectedIds.includes(detail.id)) setDetail(null);
      setSelected({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Bulk-Löschen");
    }
  };

  const removeRecipe = async (r) => {
    const ok = await confirm({
      title: `Rezeptur „${r.name}" löschen?`,
      description:
        "Die Rezeptur wird endgültig entfernt. Bereits produzierte Chargen und Lagerbestände bleiben unverändert. Laufende oder abgeschlossene Produktionsaufträge behalten ihre bestehenden Daten.",
      confirmLabel: "Endgültig löschen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/recipes/${r.id}`);
      toast.success(`Rezeptur „${r.name}" gelöscht`);
      if (detail?.id === r.id) setDetail(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.message || JSON.stringify(d)) : (d || "Fehler beim Löschen"));
    }
  };

  const resetForm = () => {
    setForm({ name: "", output_item_id: "", output_quantity: 1, output_unit: "g", notes: "", bag_item_id: "" });
    setIngs([{ item_id: "", quantity: "", unit: "kg" }]);
    setEditingId(null);
  };

  const openEdit = async (r) => {
    const { data } = await api.get(`/recipes/${r.id}`);
    setForm({
      name: data.name || "",
      output_item_id: data.output_item_id || "",
      output_quantity: data.output_quantity ?? 1,
      output_unit: data.output_unit || "g",
      notes: data.notes || "",
      bag_item_id: data.bag_item_id || "",
    });
    setIngs((data.ingredients || []).map((i) => ({
      item_id: i.item_id, quantity: i.quantity, unit: i.unit || "kg",
    })));
    if ((data.ingredients || []).length === 0) {
      setIngs([{ item_id: "", quantity: "", unit: "kg" }]);
    }
    setEditingId(r.id);
    setOpen(true);
  };

  const duplicateRecipe = async (r) => {
    const { data } = await api.get(`/recipes/${r.id}`);
    // Runde 126.10 – Prozente einfrieren.
    // Beim Duplizieren berechnen wir aus den Original-Mengen den fixen %-Anteil je Zutat.
    // Ändert der Nutzer danach die Ausbringung, werden die Mengen aus (%-Anteil × neuer
    // Gesamtcharge) neu berechnet — die Prozente selbst bleiben gelockt, bis der Nutzer
    // sie explizit entsperrt.
    const outUnit = (data.output_unit || "g").toLowerCase();
    const outQty = Number(data.output_quantity) || 0;
    const outItem = items.find((it) => it.id === data.output_item_id);
    const totalG = outUnit === "g"
      ? outQty
      : outUnit === "stk"
        ? outQty * (Number(outItem?.weight_g) || 0)
        : 0;
    const toG = (q, u) => {
      const n = Number(q);
      if (!isFinite(n)) return 0;
      const uu = (u || "g").toLowerCase();
      if (uu === "kg") return n * 1000;
      if (uu === "g") return n;
      if (uu === "l") return n * 1000;
      if (uu === "ml") return n;
      return 0;
    };
    setForm({
      name: `${data.name || "Rezeptur"} (Kopie)`,
      output_item_id: data.output_item_id || "",
      output_quantity: data.output_quantity ?? 1,
      output_unit: data.output_unit || "g",
      notes: data.notes || "",
      bag_item_id: data.bag_item_id || "",
      scale_ings: true,
      dup_locked: totalG > 0,   // Prozent-Feld erst locken wenn Basis rechenbar ist
    });
    setIngs((data.ingredients || []).map((i) => {
      const g = toG(i.quantity, i.unit);
      const pct = totalG > 0 && g > 0 ? +(g / totalG * 100).toFixed(6) : 0;
      return { item_id: i.item_id, quantity: i.quantity, unit: i.unit || "g", _pct: pct };
    }));
    if ((data.ingredients || []).length === 0) {
      setIngs([{ item_id: "", quantity: "", unit: "g", _pct: 0 }]);
    }
    setEditingId(null);
    setOpen(true);
    toast.info("Duplikat vorbereitet – Prozente sind gelockt, Menge folgt der Ausbringung");
  };

  const openScale = async (r) => {
    const { data } = await api.get(`/recipes/${r.id}`);
    setScaleDlg({ recipe: data, factor: "2", name: "" });
  };

  const submitScale = async () => {
    if (!scaleDlg) return;
    const factor = Number(scaleDlg.factor);
    if (!(factor > 0)) { toast.error("Faktor muss größer als 0 sein"); return; }
    try {
      const payload = { factor };
      if (scaleDlg.name.trim()) payload.name = scaleDlg.name.trim();
      const { data } = await api.post(`/recipes/${scaleDlg.recipe.id}/scale`, payload);
      toast.success(`Skaliert x${factor} → ${data.name}`);
      setScaleDlg(null);
      load();
      openDetail(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const load = async () => setRecipes((await api.get("/recipes")).data);
  useEffect(() => { load(); api.get("/items").then((r) => setItems(r.data)); }, []);

  // rawItems intentionally includes both `raw` and `packaging` — recipes
  // can carry Verpackungen (Beutel, Etiketten, Kartons) alongside Zutaten.
  const rawItems = items.filter((i) => i.type !== "finished");
  const finishedItems = items.filter((i) => i.type === "finished");

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        output_quantity: Number(form.output_quantity),
        bag_item_id: form.bag_item_id || null,
        ingredients: ings
          .filter((i) => i.item_id && Number(i.quantity) > 0)
          .map((i) => ({ item_id: i.item_id, quantity: Number(i.quantity), unit: i.unit })),
      };
      if (editingId) {
        await api.patch(`/recipes/${editingId}`, payload);
        toast.success("Rezeptur aktualisiert");
        if (detail?.id === editingId) openDetail(editingId);
      } else {
        await api.post("/recipes", payload);
        toast.success("Rezeptur angelegt");
      }
      setOpen(false); resetForm();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/recipes/${id}`);
    setDetail(data);
  };

  return (
    <div data-testid="page-recipes">
      <PageHeader
        eyebrow="Produktentwicklung"
        title="Rezepturen & Stücklisten"
        description="Wie viel von was ergibt eine POPCRN Sorte? Das Fundament der Produktion."
        actions={
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <Button variant="outline" data-testid="btn-bulk-delete-recipes"
                      onClick={bulkDelete}
                      className="rounded-full border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 size={14} className="mr-1" /> {selectedIds.length}× löschen
              </Button>
            )}
            <Button variant="outline" className="rounded-full" data-testid="btn-import-recipes"
                    onClick={() => setImportOpen(true)}>
              <FileUp size={16} className="mr-1" /> Importieren
            </Button>
            <label className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs font-semibold border border-[var(--border)] bg-white hover:bg-[var(--pop-cream)]/40 cursor-pointer"
                   data-testid="btn-import-csv-recipe" title="Ein oder mehrere Rezepte aus CSV importieren (POPCRN-Schema)">
              <Upload size={14} /> CSV
              <input type="file" accept=".csv" multiple hidden onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                const fd = new FormData();
                for (const f of files) fd.append("files", f);
                try {
                  const { data } = await api.post("/recipes/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" }});
                  if (data.results) {
                    // Multi-file response
                    const ok = data.success, err = data.errors;
                    toast.success(`${ok} Rezept(e) importiert${err ? `, ${err} Fehler` : ""}`);
                    for (const r of data.results) {
                      if (r.error) toast.error(`${r.filename}: ${r.error}`);
                      else if (r.cost_per_batch != null) toast.info(`${r.name}: ${r.cost_per_batch.toFixed(2)}€ Gestehungskosten · VP-Vorschlag ${r.suggested_sale_price?.toFixed(2)}€`);
                    }
                  } else {
                    toast.success(`${data.action === "create" ? "Erstellt" : "Aktualisiert"}: ${data.name} · ${data.ingredients.length} Zutaten${data.ingredients_created ? ` (${data.ingredients_created} neu)` : ""}`);
                    if (data.cost_per_batch != null && data.cost_per_batch > 0) {
                      toast.info(`Gestehungskosten: ${data.cost_per_batch.toFixed(2)}€ · Verkaufspreis-Vorschlag: ${data.suggested_sale_price?.toFixed(2)}€ (3× Marge)`);
                    }
                  }
                  load();
                } catch (err) { toast.error(err.response?.data?.detail || "Import fehlgeschlagen"); }
                e.target.value = "";
              }} />
            </label>
            <Button variant="outline" onClick={async () => {
              try {
                const res = await api.get("/recipes/import-csv/template", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                const a = document.createElement("a"); a.href = url; a.download = "popcrn-rezept-vorlage.csv"; a.click();
                setTimeout(() => URL.revokeObjectURL(url), 30000);
              } catch (err) { toast.error("Vorlage konnte nicht geladen werden"); }
            }} data-testid="btn-download-template" className="rounded-full text-xs" title="POPCRN-Rezept-CSV-Vorlage herunterladen">
              <FileUp size={14} className="mr-1" /> Vorlage.csv
            </Button>
            <Button variant="outline" onClick={async () => {
              try {
                const { data } = await api.post("/recipes/ensure-standard-ingredients");
                toast.success(`${data.created_count} Rohstoffe neu angelegt · ${data.existing_count} bereits vorhanden`);
                load();
              } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
            }} data-testid="btn-seed-ingredients" className="rounded-full text-xs" title="27 POPCRN-Standard-Rohstoffe anlegen (idempotent)">
              <Beaker size={14} className="mr-1" /> Standard-Rohstoffe
            </Button>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="btn-new-recipe" onClick={resetForm} className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                  <Plus size={16} className="mr-1" /> Neue Rezeptur
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Rezeptur bearbeiten" : "Neue Rezeptur"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Name *</Label><Input required data-testid="input-recipe-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div>
                    <Label>Fertigware *</Label>
                    <ItemCombobox
                      items={finishedItems}
                      value={form.output_item_id}
                      onChange={(v) => setForm({ ...form, output_item_id: v })}
                      placeholder="Fertigware wählen…"
                      testId="select-recipe-output"
                    />
                  </div>
                  <div>
                    <Label>Tüte (Grundverpackung)</Label>
                    <ItemCombobox
                      items={items.filter((i) => i.type === "packaging")}
                      value={form.bag_item_id}
                      onChange={(v) => setForm({ ...form, bag_item_id: v })}
                      placeholder="Tüten-Artikel wählen (optional)…"
                      testId="select-recipe-bag"
                    />
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                      1 Tüte je produzierte Einheit. Wird beim Abschluss automatisch als Verpackung abgebucht.
                    </div>
                  </div>
                  <div>
                    <Label>Ausbringung / Packungsgröße</Label>
                    <div className="grid grid-cols-[1fr_110px] gap-2">
                      <Input type="number" step="any"
                             data-testid="input-recipe-output-qty"
                             value={form.output_quantity}
                             placeholder="z.B. 100 (Punkt)"
                             onChange={(e) => {
                               const raw = normalizeDecimal(e.target.value);
                               const next = Number(raw);
                               const prev = Number(form.output_quantity);
                               setForm({ ...form, output_quantity: raw });
                               // Runde 126.10 – Duplikat-Modus: fixe %-Anteile beibehalten,
                               // Mengen aus (%-Anteil × neues Gesamtgewicht) neu berechnen.
                               if (form.dup_locked && isFinite(next) && next > 0) {
                                 const outUnit = (form.output_unit || "g").toLowerCase();
                                 const outItem = items.find((it) => it.id === form.output_item_id);
                                 const newTotalG = outUnit === "g" ? next
                                   : outUnit === "stk" ? next * (Number(outItem?.weight_g) || 0)
                                   : 0;
                                 if (newTotalG > 0) {
                                   setIngs((cur) => cur.map((it) => {
                                     const pct = Number(it._pct) || 0;
                                     if (pct <= 0) return it;
                                     const g = +(pct / 100 * newTotalG).toFixed(6);
                                     return { ...it, quantity: String(g), unit: "g" };
                                   }));
                                   return;
                                 }
                               }
                               // Zutaten automatisch mitskalieren, wenn Switch aktiv und beide Werte gültig
                               if (form.scale_ings && isFinite(next) && next > 0 && isFinite(prev) && prev > 0 && next !== prev) {
                                 const factor = next / prev;
                                 setIngs((cur) => cur.map((it) => ({
                                   ...it,
                                   quantity: it.quantity === "" || it.quantity == null
                                     ? it.quantity
                                     : String(+(Number(it.quantity) * factor).toFixed(6)),
                                 })));
                               }
                             }} />
                      <Select value={form.output_unit || "g"}
                              onValueChange={(v) => setForm({ ...form, output_unit: v })}>
                        <SelectTrigger data-testid="select-recipe-output-unit"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="g">g</SelectItem>
                          <SelectItem value="stk">Stück</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-xs text-[var(--text-muted)] mt-1" data-testid="output-summary">
                      {(() => {
                        const q = Number(form.output_quantity) || 0;
                        const u = (form.output_unit || "g").toLowerCase();
                        const out = items.find((it) => it.id === form.output_item_id);
                        if (q <= 0) return "Packungsgröße pro Einheit — z.B. 100 g Beutel oder 250 ml Flasche.";
                        if (u === "stk") {
                          const w = Number(out?.weight_g) || 0;
                          if (w > 0) {
                            const totalG = q * w;
                            const totalKg = totalG / 1000;
                            return `1 Rezept ergibt ${q} Packung${q === 1 ? "" : "en"} à ${w} g = ${totalKg.toLocaleString("de-DE", { maximumFractionDigits: 3 })} kg Gesamtgewicht.`;
                          }
                          return `1 Rezept ergibt ${q} Packung${q === 1 ? "" : "en"}. Hinweis: „Gewicht je Einheit" beim Fertigartikel pflegen für Gesamtgewicht-Anzeige.`;
                        }
                        if (u === "g")  return `1 Rezept ergibt ${q} g = ${(q / 1000).toLocaleString("de-DE", { maximumFractionDigits: 3 })} kg.`;
                        if (u === "kg") return `1 Rezept ergibt ${q} kg = ${(q * 1000).toLocaleString("de-DE")} g.`;
                        if (u === "ml") return `1 Rezept ergibt ${q} ml = ${(q / 1000).toLocaleString("de-DE", { maximumFractionDigits: 3 })} l.`;
                        if (u === "l")  return `1 Rezept ergibt ${q} l = ${(q * 1000).toLocaleString("de-DE")} ml.`;
                        return `1 Rezept ergibt ${q} ${u}.`;
                      })()}
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="overline">Zutaten</Label>
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => setIngs([...ings, { item_id: "", quantity: "", unit: "g" }])}
                      data-testid="btn-add-ingredient" className="rounded-full">
                      <Plus size={14} /> Zutat
                    </Button>
                  </div>

                  {/* Percent-based ingredient editor.  User types %-share only,
                      quantity + unit + price are computed from the recipe's
                      total output weight (g). */}
                  {(() => {
                    const outUnit = (form.output_unit || "g").toLowerCase();
                    const outQty = Number(form.output_quantity) || 0;
                    // Total batch weight in grams (only meaningful for weight-based recipes)
                    const outItem = items.find((it) => it.id === form.output_item_id);
                    const totalG = outUnit === "g"
                      ? outQty
                      : outUnit === "stk"
                        ? outQty * (Number(outItem?.weight_g) || 0)
                        : 0;
                    return (
                      <>
                        {totalG > 0 ? (
                          <div className="mb-2 text-[11px] text-[var(--text-muted)]">
                            Gesamtgewicht der Charge: <span className="font-mono font-bold text-[var(--pop-black)]">
                            {totalG.toLocaleString("de-DE")} g</span>. Prozentanteile werden automatisch in Menge (g/ml) umgerechnet.
                          </div>
                        ) : (
                          <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-[11px] px-2 py-1.5">
                            Für Prozent-Modus die Ausbringung als g eintragen (oder Fertigartikel mit „Gewicht je Einheit").
                          </div>
                        )}

                        {/* Column headers */}
                        <div className="grid grid-cols-[minmax(280px,2fr)_90px_140px_minmax(140px,180px)_36px] gap-3 items-center min-w-[720px] px-1 pb-1 mb-1 border-b border-[var(--border)]">
                          <div className="overline text-[10px]">Rohstoff</div>
                          <div className="overline text-[10px] text-right">%</div>
                          <div className="overline text-[10px] text-right">Menge (berechnet)</div>
                          <div className="overline text-[10px] text-right">Preis</div>
                          <div />
                        </div>
                        {form.dup_locked && (
                          <div className="mb-2 rounded-lg bg-pink-50 border border-pink-200 text-pink-900 text-[11px] px-2 py-1.5 flex items-center justify-between gap-2" data-testid="dup-lock-banner">
                            <span>Prozente aus Original-Rezeptur <b>gelockt</b> — Menge folgt der Ausbringung. Zum Anpassen bitte entsperren.</span>
                            <button type="button" data-testid="btn-unlock-percent"
                                    onClick={() => setForm((f) => ({ ...f, dup_locked: false }))}
                                    className="rounded-full bg-pink-600 text-white text-[11px] font-bold px-3 py-0.5 hover:bg-pink-700">
                              Prozente entsperren
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  <div className="space-y-2 overflow-x-auto">
                    {ings.map((ing, idx) => {
                      const raw = items.find((it) => it.id === ing.item_id);
                      const outUnit = (form.output_unit || "g").toLowerCase();
                      const outQty = Number(form.output_quantity) || 0;
                      const outItem = items.find((it) => it.id === form.output_item_id);
                      const totalG = outUnit === "g"
                        ? outQty
                        : outUnit === "stk"
                          ? outQty * (Number(outItem?.weight_g) || 0)
                          : 0;

                      // Derive percent from stored quantity/unit (in grams) vs. totalG.
                      // Prefer explicit _percent if the user just typed it.
                      const storedG = (() => {
                        const q = Number(String(ing.quantity ?? "").replace(",", "."));
                        if (!isFinite(q) || q <= 0) return 0;
                        const u = (ing.unit || "g").toLowerCase();
                        if (u === "kg") return q * 1000;
                        if (u === "g") return q;
                        if (u === "mg") return q / 1000;
                        if (u === "l") return q * 1000;   // ≈ ml for water-density fallback
                        if (u === "ml") return q;
                        return 0; // stk → cannot map to g-share
                      })();
                      const derivedPct = totalG > 0 && storedG > 0 ? (storedG / totalG) * 100 : 0;
                      const shownPct = ing._percent != null && ing._percent !== ""
                        ? ing._percent
                        : (derivedPct > 0 ? derivedPct.toFixed(2) : "");

                      const conv = convertIngredientToItemBase(ing.quantity, ing.unit, raw);
                      const perBase = itemCostPerBase(raw);
                      const cost = (conv.value != null && perBase > 0) ? conv.value * perBase : 0;
                      const isPack = (raw?.type || "").toLowerCase() === "packaging";

                      // Displayed computed quantity (in item's base unit)
                      const computedGrams = totalG > 0 && Number(shownPct) > 0
                        ? (totalG * Number(String(shownPct).replace(",", "."))) / 100
                        : storedG;
                      const displayUnit = raw ? itemBase(raw) : "g";
                      const displayQty = (() => {
                        if (!raw || !computedGrams) return "";
                        // Convert g → item's base unit
                        if (displayUnit === "g" || displayUnit === "") return computedGrams;
                        if (displayUnit === "ml") {
                          const d = Number(raw.density);
                          return d > 0 ? computedGrams / d : computedGrams; // ≈ 1:1 fallback
                        }
                        if (displayUnit === "stk") return computedGrams; // rare edge case
                        return computedGrams;
                      })();

                      const onPercentChange = (val) => {
                        const c = [...ings];
                        const normalized = normalizeDecimal(val);
                        c[idx]._percent = normalized;
                        const pctNum = Number(String(normalized).replace(",", "."));
                        if (isFinite(pctNum) && pctNum > 0 && totalG > 0 && raw) {
                          const g = (totalG * pctNum) / 100;
                          const baseUnit = itemBase(raw) || "g";
                          if (baseUnit === "ml") {
                            const d = Number(raw.density);
                            c[idx].quantity = String(+(d > 0 ? g / d : g).toFixed(4));
                            c[idx].unit = "ml";
                          } else if (baseUnit === "stk") {
                            c[idx].quantity = String(+g.toFixed(4));
                            c[idx].unit = "stk";
                          } else {
                            c[idx].quantity = String(+g.toFixed(4));
                            c[idx].unit = "g";
                          }
                        } else {
                          c[idx].quantity = "";
                        }
                        setIngs(c);
                      };

                      return (
                      <div key={idx} className="grid grid-cols-[minmax(280px,2fr)_90px_140px_minmax(140px,180px)_36px] gap-3 items-center min-w-[720px]">
                        <ItemCombobox
                          items={rawItems}
                          value={ing.item_id}
                          onChange={(v) => {
                            const c = [...ings];
                            c[idx].item_id = v;
                            // Re-apply percent so the newly-picked item lands in its own base unit
                            const p = Number(String(c[idx]._percent ?? "").replace(",", "."));
                            const newRaw = items.find((it) => it.id === v);
                            if (isFinite(p) && p > 0 && totalG > 0 && newRaw) {
                              const g = (totalG * p) / 100;
                              const baseUnit = itemBase(newRaw) || "g";
                              if (baseUnit === "ml") {
                                const d = Number(newRaw.density);
                                c[idx].quantity = String(+(d > 0 ? g / d : g).toFixed(4));
                                c[idx].unit = "ml";
                              } else {
                                c[idx].quantity = String(+g.toFixed(4));
                                c[idx].unit = baseUnit || "g";
                              }
                            }
                            setIngs(c);
                          }}
                          placeholder="Rohstoff wählen…"
                          testId={`select-ing-${idx}`}
                        />
                        <Input type="number" step="any" placeholder={isPack ? "n/a" : "%"}
                               className="font-mono text-right"
                               value={isPack ? "" : shownPct}
                               disabled={isPack || totalG <= 0}
                               readOnly={form.dup_locked && !isPack}
                               title={form.dup_locked ? 'Prozente aus Duplikat gelockt – zum Bearbeiten „Prozente entsperren" klicken' : undefined}
                               data-testid={`input-ing-percent-${idx}`}
                               onChange={(e) => { if (form.dup_locked) return; onPercentChange(e.target.value); }} />
                        {isPack ? (
                          <div className="flex items-center gap-1" data-testid={`ing-pack-qty-${idx}`}>
                            <Input type="number" step="any" placeholder="Stk"
                                   className="font-mono text-right"
                                   value={ing.quantity ?? ""}
                                   onChange={(e) => {
                                     const c = [...ings];
                                     c[idx].quantity = normalizeDecimal(e.target.value);
                                     c[idx].unit = "stk";
                                     c[idx]._percent = "";
                                     setIngs(c);
                                   }} />
                            <span className="pop-pill bg-sky-100 text-sky-800 text-[9px] font-bold shrink-0">VERP</span>
                          </div>
                        ) : (
                          <div className="font-mono text-right text-sm px-2 py-1 rounded-md bg-[var(--pop-cream)]/40 border border-[var(--border)] whitespace-nowrap"
                               data-testid={`ing-computed-qty-${idx}`}
                               title={raw ? `Basis-Einheit des Rohstoffs: ${displayUnit}` : "Rohstoff wählen"}>
                            {displayQty && Number(displayQty) > 0
                              ? `${Number(displayQty).toLocaleString("de-DE", { maximumFractionDigits: 2 })} ${displayUnit}`
                              : <span className="text-[var(--text-muted)]">—</span>}
                          </div>
                        )}
                        <div className="text-right font-mono text-xs whitespace-nowrap" data-testid={`ing-cost-${idx}`}>
                          <div className="flex items-center justify-end gap-1.5">
                            {conv.needsDensity ? (
                              <span data-testid={`ing-needs-density-${idx}`}
                                    className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 text-[11px] font-semibold"
                                    title={`Dichte (g/ml) fehlt im Artikel „${raw?.name || "?"}". Ohne Dichte kann ${ing.unit} nicht in ${itemBase(raw)} umgerechnet werden.`}>
                                <AlertTriangle size={12} /> Dichte
                              </span>
                            ) : cost > 0 ? (
                              <span className="text-[var(--pop-pink)] font-bold">{cost.toFixed(2)} €</span>
                            ) : (
                              <span className="text-[var(--text-muted)]">— €</span>
                            )}
                          </div>
                          <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                            {raw?.purchase_price ? (
                              raw?.container_size ? (
                                `${Number(raw.purchase_price).toFixed(2)} €/${raw.container_size}${raw.container_unit || raw.unit || "kg"}`
                              ) : `${Number(raw.purchase_price).toFixed(2)} €/${raw.unit || "kg"}`
                            ) : "kein EK"}
                          </div>
                        </div>
                        <button type="button" onClick={() => setIngs(ings.filter((_, i) => i !== idx))}
                                className="text-red-500 hover:text-red-700 flex items-center justify-center">
                          <Trash2 size={16} />
                        </button>
                      </div>
                      );
                    })}
                  </div>
                  {(() => {
                    let missingDensity = 0;
                    const totalRaw = ings.reduce((sum, ing) => {
                      const raw = items.find((it) => it.id === ing.item_id);
                      const conv = convertIngredientToItemBase(ing.quantity, ing.unit, raw);
                      if (conv.needsDensity) { missingDensity += 1; return sum; }
                      const perBase = itemCostPerBase(raw);
                      return sum + ((conv.value || 0) * perBase);
                    }, 0);
                    const outQty = Number(form.output_quantity) || 1;
                    const perUnit = totalRaw / outQty;
                    const outItem = items.find((it) => it.id === form.output_item_id);
                    const vk = Number(outItem?.price) || 0;
                    const margin = vk - perUnit;
                    const marginPct = vk > 0 ? (margin / vk) * 100 : 0;
                    return (
                    <div className="mt-3 rounded-xl bg-[var(--pop-pink)]/5 border border-[var(--pop-pink)]/30 p-3 grid grid-cols-4 gap-2 text-xs" data-testid="recipe-cost-preview">
                      <div><div className="overline text-[10px]">Rohstoffe gesamt</div>
                        <div className="font-mono font-bold text-lg">{totalRaw.toFixed(2)} €</div>
                        {missingDensity > 0 && (
                          <div className="text-[10px] text-orange-700 font-semibold" data-testid="cost-missing-density">
                            ⚠ {missingDensity} Zutat/-en ohne Dichte
                          </div>
                        )}</div>
                      <div><div className="overline text-[10px]">Kosten je Einheit</div>
                        <div className="font-mono font-bold text-lg">{perUnit.toFixed(2)} €</div></div>
                      <div><div className="overline text-[10px]">VK ({outItem?.name || "—"})</div>
                        <div className="font-mono font-bold text-lg">{vk > 0 ? `${vk.toFixed(2)} €` : "—"}</div></div>
                      <div><div className="overline text-[10px]">Marge</div>
                        <div className={`font-mono font-bold text-lg ${margin >= 0 ? "text-green-700" : "text-red-700"}`}>
                          {vk > 0 ? `${margin.toFixed(2)} € (${marginPct.toFixed(0)} %)` : "—"}
                        </div></div>
                    </div>
                    );
                  })()}
                </div>

                <DialogFooter>
                  <Button type="submit" data-testid="btn-save-recipe" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
                </DialogFooter>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
          <Table className="table-zebra">
            <TableHeader>
              <TableRow>
                <TableHead className="overline w-10">
                  <button onClick={toggleAll} data-testid="recipes-select-all"
                          className="hover:opacity-70" title="Alle auswählen">
                    {recipes.length > 0 && recipes.every((r) => selected[r.id])
                      ? <CheckSquare size={16} className="text-[var(--pop-pink)]" />
                      : <Square size={16} />}
                  </button>
                </TableHead>
                <TableHead className="overline">Rezeptur</TableHead>
                <TableHead className="overline">Ergebnis</TableHead>
                <TableHead className="overline text-right">Menge</TableHead>
                <TableHead className="overline text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipes.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => openDetail(r.id)} data-testid={`recipe-row-${r.id}`}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => toggleSelect(r.id)}
                            data-testid={`select-recipe-${r.id}`}
                            className="hover:opacity-70">
                      {selected[r.id]
                        ? <CheckSquare size={16} className="text-[var(--pop-pink)]" />
                        : <Square size={16} />}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium flex items-center gap-2"><ChefHat size={14} className="text-[var(--pop-caramel)]" />{r.name}</TableCell>
                  <TableCell><div className="text-sm">{r.output_item_name}</div><div className="text-xs font-mono text-[var(--text-muted)]">{r.output_item_sku}</div></TableCell>
                  <TableCell className="font-mono text-xs text-right">{r.output_quantity}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEdit(r)} data-testid={`btn-edit-recipe-${r.id}`}
                            title="Rezeptur bearbeiten"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)] hover:underline mr-3">
                      <Pencil size={12} /> Bearbeiten
                    </button>
                    <button onClick={() => duplicateRecipe(r)} data-testid={`btn-duplicate-recipe-${r.id}`}
                            title="Rezeptur duplizieren"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-caramel)] hover:underline mr-3">
                      <Copy size={12} /> Duplizieren
                    </button>
                    <button onClick={() => openScale(r)} data-testid={`btn-scale-recipe-${r.id}`}
                            title="Rezeptur skalieren (Faktor x)"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)] hover:underline">
                      <Scaling size={12} /> Skalieren
                    </button>
                    <button onClick={() => removeRecipe(r)} data-testid={`btn-delete-recipe-${r.id}`}
                            title="Rezeptur löschen"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline ml-3">
                      <Trash2 size={12} /> Löschen
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              {recipes.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-[var(--text-muted)]">Keine Rezepturen.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="bg-white border border-[var(--border)] rounded-2xl p-6" data-testid="recipe-detail">
          {detail ? (
            <>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="overline mb-1">Rezeptur</div>
                  <h2 className="font-display font-black text-2xl">{detail.name}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-full"
                          data-testid="btn-edit-recipe-detail"
                          onClick={() => openEdit(detail)}>
                    <Pencil size={12} className="mr-1" /> Bearbeiten
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full"
                          data-testid="btn-duplicate-recipe-detail"
                          onClick={() => duplicateRecipe(detail)}>
                    <Copy size={12} className="mr-1" /> Duplizieren
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full"
                          data-testid="btn-scale-recipe-detail"
                          onClick={() => openScale(detail)}>
                    <Scaling size={12} className="mr-1" /> Skalieren
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
                          data-testid="btn-delete-recipe-detail"
                          onClick={() => removeRecipe(detail)}>
                    <Trash2 size={12} className="mr-1" /> Löschen
                  </Button>
                </div>
              </div>
              <div className="text-sm text-[var(--text-muted)] mb-4">
                → {detail.output_item_name} · {detail.output_quantity} {detail.output_unit || "Einheit(en)"} pro Charge
              </div>

              <BatchScalePanel recipe={detail} items={items} />

              <div className="mt-6 pt-4 border-t border-[var(--border)]">
                <div className="overline mb-2">Zutaten (Basis-Rezept)</div>
                <ul className="divide-y divide-[var(--border)]">
                  {detail.ingredients?.map((ing, i) => (
                    <li key={i} className="py-2 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">{ing.item_name}</div>
                        <div className="text-xs font-mono text-[var(--text-muted)]">{ing.item_sku} · Bestand {ing.stock ?? 0}</div>
                      </div>
                      <div className="font-mono text-sm">{ing.quantity} {ing.unit}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="text-sm text-[var(--text-muted)]">Wähle links eine Rezeptur, um Details zu sehen.</div>
          )}
        </div>
      </div>

      {/* Skalieren Dialog */}
      <Dialog open={!!scaleDlg} onOpenChange={(o) => !o && setScaleDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-scale-recipe">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Scaling size={22} className="text-[var(--pop-pink)]" />
              Rezeptur skalieren
            </DialogTitle>
          </DialogHeader>
          {scaleDlg && (
            <div className="space-y-4">
              <div className="rounded-xl bg-[var(--pop-cream)]/40 p-3 text-sm">
                <div className="overline text-xs mb-1">Basisrezept</div>
                <div className="font-semibold">{scaleDlg.recipe.name}</div>
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  → {scaleDlg.recipe.output_item_name} · {scaleDlg.recipe.output_quantity} Einheit(en)
                </div>
              </div>
              <div>
                <Label>Faktor *</Label>
                <Input type="number" step="any" min="0.1" value={scaleDlg.factor}
                       data-testid="input-scale-factor"
                       onChange={(e) => setScaleDlg({ ...scaleDlg, factor: e.target.value })}
                       placeholder="z.B. 2.5" />
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  Multipliziert Ausbringung UND alle Zutatenmengen. Beispiel: 2 = doppelte Charge.
                </div>
              </div>
              <div>
                <Label>Neuer Name (optional)</Label>
                <Input value={scaleDlg.name}
                       data-testid="input-scale-name"
                       onChange={(e) => setScaleDlg({ ...scaleDlg, name: e.target.value })}
                       placeholder={`${scaleDlg.recipe.name} (x${scaleDlg.factor || "?"})`} />
              </div>
              {Number(scaleDlg.factor) > 0 && (
                <div className="rounded-xl border border-[var(--border)] p-3 text-sm" data-testid="scale-preview">
                  <div className="overline text-xs mb-2">Vorschau (skaliert)</div>
                  <div className="mb-2">
                    <span className="text-[var(--text-muted)]">Ausbringung: </span>
                    <span className="font-mono font-bold">
                      {(Number(scaleDlg.recipe.output_quantity || 1) * Number(scaleDlg.factor)).toFixed(2)}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {(scaleDlg.recipe.ingredients || []).map((ing, i) => (
                      <li key={i} className="flex items-center justify-between text-xs">
                        <span>{ing.item_name || ing.item_sku}</span>
                        <span className="font-mono">
                          {(Number(ing.quantity || 0) * Number(scaleDlg.factor)).toFixed(2)} {ing.unit || "kg"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setScaleDlg(null)} className="rounded-full">Abbrechen</Button>
                <Button onClick={submitScale} data-testid="btn-confirm-scale"
                        className="rounded-full bg-[var(--pop-pink)] text-white hover:opacity-90 font-bold">
                  Als neue Rezeptur speichern
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        entityLabel="Rezepturen"
        previewEndpoint="/recipes/import/preview"
        commitEndpoint="/recipes/import/commit"
        templateUrl={`${API_BASE}/recipes/import/template.xlsx`}
        previewKey="groups"
        onDone={() => load()}
        renderPreviewHeader={() => (
          <>
            <th className="text-left p-2">Rezeptur</th>
            <th className="text-left p-2">Fertigware</th>
            <th className="text-right p-2">Ausbr.</th>
            <th className="text-right p-2">Zutaten</th>
            <th className="text-left p-2">Aktion</th>
            <th className="text-left p-2">Hinweis</th>
          </>
        )}
        renderPreviewRow={(g, i) => {
          const badgeCls = g.action === "create"
            ? "bg-green-100 text-green-700"
            : g.action === "update" ? "bg-blue-100 text-blue-700"
            : "bg-red-100 text-red-700";
          const foundCount = g.ingredients.filter((x) => x.found).length;
          const fuzzyCount = g.ingredients.filter((x) => x.match_type === "fuzzy").length;
          const missCount = g.ingredients.length - foundCount;
          return (
            <>
              <tr key={i} className="border-t border-[var(--border)]" data-testid={`import-rec-row-${i}`}>
                <td className="p-2">{g.output_name || g.output_sku}</td>
                <td className="p-2 font-mono text-[var(--text-muted)]">
                  {g.output_item_name || "—"}
                  {g.output_match_type === "fuzzy" && (
                    <span className="ml-1 pop-pill bg-amber-100 text-amber-700 text-[9px] font-bold" data-testid={`import-rec-fuzzy-out-${i}`}>
                      Fuzzy {(g.output_match_score * 100).toFixed(0)}%
                    </span>
                  )}
                </td>
                <td className="p-2 text-right font-mono">{g.output_qty ?? "—"}{g.output_unit ? ` ${g.output_unit}` : ""}</td>
                <td className="p-2 text-right font-mono">
                  {foundCount}/{g.ingredients.length}
                  {fuzzyCount > 0 && (
                    <span className="ml-1 pop-pill bg-amber-100 text-amber-700 text-[9px] font-bold" data-testid={`import-rec-fuzzy-count-${i}`}>
                      🔍 {fuzzyCount}
                    </span>
                  )}
                  {missCount > 0 && <span className="ml-1 text-red-700 text-[10px]">({missCount} fehlen)</span>}
                </td>
                <td className="p-2"><span className={`pop-pill text-[10px] font-bold ${badgeCls}`}>{g.action}</span></td>
                <td className="p-2 text-red-700 text-[11px]">{g.errors?.join("; ") || (g.existing_name ? `→ ${g.existing_name}` : "")}</td>
              </tr>
              {/* Show fuzzy-matched ingredients on hover-expandable sub-row */}
              {fuzzyCount > 0 && (
                <tr key={`${i}-fuzzy`} className="bg-amber-50/40" data-testid={`import-rec-fuzzy-detail-${i}`}>
                  <td colSpan={6} className="p-2 pl-6 text-[11px] text-amber-800">
                    <span className="font-semibold">Auto-Match:</span>{" "}
                    {g.ingredients.filter((x) => x.match_type === "fuzzy").map((x, k) => (
                      <span key={k} className="inline-block mr-3">
                        &laquo;{x.name || x.sku}&raquo; → <span className="font-mono">{x.item_name}</span>{" "}
                        <span className="text-amber-600">({(x.match_score * 100).toFixed(0)}%)</span>
                      </span>
                    ))}
                  </td>
                </tr>
              )}
            </>
          );
        }}
      />
    </div>
  );
}


// -----------------------------------------------------------------------------
// Charge skalieren (Runde 124) – Live-Skalierung in der Rezeptur-Detailansicht
// -----------------------------------------------------------------------------
const UNIT_TO_G = { g: 1, kg: 1000, mg: 0.001, ml: 1, l: 1000 };
function ingredientToGrams(qty, unit) {
  const u = (unit || "g").toLowerCase();
  const f = UNIT_TO_G[u];
  if (f == null) return null; // stk / non-weighable
  return Number(qty || 0) * f;
}

function fmtDe(n, digits = 2) {
  return Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function fmtWeight(grams) {
  const g = Number(grams || 0);
  if (g >= 1000) return `${fmtDe(g / 1000, 3)} kg`;
  return `${fmtDe(g, 2)} g`;
}

function BatchScalePanel({ recipe, items = [] }) {
  const [mode, setMode] = useState("weight");   // "weight" | "units"
  const [target, setTarget] = useState("");
  const [busyPdf, setBusyPdf] = useState(false);

  // Item lookup by id for quick cost/density lookups.
  const itemById = useMemo(() => {
    const m = {};
    for (const it of items) m[it.id] = it;
    return m;
  }, [items]);

  // Base weight of the recipe = sum of weighable ingredients in grams
  const baseWeightG = useMemo(() => {
    let sum = 0;
    for (const ing of recipe.ingredients || []) {
      const g = ingredientToGrams(ing.quantity, ing.unit);
      if (g != null) sum += g;
    }
    return sum;
  }, [recipe.ingredients]);

  // Base ingredient cost (unscaled) in EUR — uses the same conversion + density
  // logic as the recipe editor so numbers match.
  const baseCostEur = useMemo(() => {
    let sum = 0;
    for (const ing of recipe.ingredients || []) {
      const raw = itemById[ing.item_id];
      if (!raw) continue;
      const conv = convertIngredientToItemBase(ing.quantity, ing.unit, raw);
      if (conv.needsDensity || conv.value == null) continue;
      const perBase = itemCostPerBase(raw);
      sum += conv.value * perBase;
    }
    return sum;
  }, [recipe.ingredients, itemById]);

  const baseUnits = Number(recipe.output_quantity || 0);
  const canWeightScale = baseWeightG > 0;
  const canUnitScale = baseUnits > 0;

  // Default target when mode changes
  useEffect(() => {
    if (mode === "weight" && canWeightScale) setTarget(String(Math.round(baseWeightG)));
    else if (mode === "units" && canUnitScale) setTarget(String(baseUnits));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, recipe.id]);

  const targetNum = Number(String(target).replace(",", ".")) || 0;
  const factor = useMemo(() => {
    if (targetNum <= 0) return 0;
    if (mode === "weight") return canWeightScale ? targetNum / baseWeightG : 0;
    return canUnitScale ? targetNum / baseUnits : 0;
  }, [mode, targetNum, baseWeightG, baseUnits, canWeightScale, canUnitScale]);

  // Scaled ingredients with percent share (relative to weighable subtotal)
  const scaled = useMemo(() => {
    return (recipe.ingredients || []).map((ing) => {
      const qty = Number(ing.quantity || 0) * factor;
      const baseG = ingredientToGrams(ing.quantity, ing.unit);
      const percent = baseWeightG > 0 && baseG != null ? (baseG / baseWeightG) * 100 : 0;
      return {
        item_name: ing.item_name || ing.item_sku || "—",
        item_sku: ing.item_sku,
        quantity: qty,
        unit: ing.unit || "g",
        percent,
        weighable: baseG != null,
      };
    });
  }, [recipe.ingredients, factor, baseWeightG]);

  const totalScaledG = useMemo(() => {
    if (mode === "weight") return targetNum;
    // In units mode, compute total scaled weight from scaled ingredients
    return scaled.reduce((sum, s) => {
      const g = ingredientToGrams(s.quantity, s.unit);
      return sum + (g || 0);
    }, 0);
  }, [mode, targetNum, scaled]);

  const exportCsv = () => {
    const rows = [
      ["Rezeptur", recipe.name],
      ["Fertigware", recipe.output_item_name || ""],
      ["Modus", mode === "weight" ? "Nach Gewicht" : "Nach Stück"],
      ["Ziel", `${targetNum} ${mode === "weight" ? "g" : "Stk"}`],
      ["Gesamtgewicht", fmtWeight(totalScaledG)],
      [],
      ["Zutat", "SKU", "Menge", "Einheit", "Anteil %"],
      ...scaled.map((s) => [
        s.item_name, s.item_sku || "",
        fmtDe(s.quantity, 2), s.unit,
        fmtDe(s.percent, 2),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => {
      const v = c == null ? "" : String(c);
      return /[";\n,]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `charge-${(recipe.name || "rezept").replace(/\s+/g, "-").toLowerCase()}-${Math.round(targetNum)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    toast.success("CSV heruntergeladen");
  };

  const exportPdf = async () => {
    if (targetNum <= 0) { toast.error("Ziel-Menge angeben"); return; }
    setBusyPdf(true);
    try {
      const res = await api.post(`/recipes/${recipe.id}/scale-pdf`,
        { mode, target: targetNum }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const msg = err.response?.data instanceof Blob
        ? await err.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return t; } })
        : (err.response?.data?.detail || err.message);
      toast.error(msg || "PDF fehlgeschlagen");
    } finally { setBusyPdf(false); }
  };

  const targetUnit = mode === "weight" ? "g" : "Stk";
  const modeUnavailable = (mode === "weight" && !canWeightScale) || (mode === "units" && !canUnitScale);

  return (
    <div className="rounded-2xl border-2 border-[var(--pop-pink)]/30 bg-[var(--pop-pink)]/5 p-4"
         data-testid="scale-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Scaling size={18} className="text-[var(--pop-pink)]" />
          <h3 className="font-display font-bold text-lg">Charge skalieren</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-full" disabled={targetNum <= 0}
                  onClick={exportCsv} data-testid="btn-scale-export-csv">
            <Download size={12} className="mr-1" /> Export CSV
          </Button>
          <Button size="sm" className="rounded-full bg-[var(--pop-pink)] text-white hover:opacity-90 font-bold"
                  disabled={targetNum <= 0 || busyPdf}
                  onClick={exportPdf} data-testid="btn-scale-export-pdf">
            <FileText size={12} className="mr-1" /> {busyPdf ? "…" : "Export PDF"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-end gap-3 mb-3">
        {/* Mode toggle */}
        <div className="inline-flex rounded-full border border-[var(--border)] bg-white overflow-hidden text-xs font-semibold"
             data-testid="scale-mode-toggle">
          <button onClick={() => setMode("weight")}
                  disabled={!canWeightScale}
                  data-testid="scale-mode-weight"
                  className={`px-4 py-2 flex items-center gap-1 transition-colors disabled:opacity-40 ${
                    mode === "weight" ? "bg-[var(--pop-pink)] text-white" : "text-[var(--text-muted)] hover:bg-[var(--pop-cream)]/50"
                  }`}>
            <Weight size={12} /> Nach Gewicht
          </button>
          <button onClick={() => setMode("units")}
                  disabled={!canUnitScale}
                  data-testid="scale-mode-units"
                  className={`px-4 py-2 flex items-center gap-1 transition-colors disabled:opacity-40 ${
                    mode === "units" ? "bg-[var(--pop-pink)] text-white" : "text-[var(--text-muted)] hover:bg-[var(--pop-cream)]/50"
                  }`}>
            <PackageIcon size={12} /> Nach Stück
          </button>
        </div>

        {/* Target input */}
        <div>
          <Label className="text-xs">
            {mode === "weight" ? "Ziel-Chargenmenge (g)" : "Anzahl Einheiten"}
          </Label>
          <div className="grid grid-cols-[1fr_60px] gap-2">
            <Input type="number" step="any" min="0" value={target}
                   data-testid="scale-target-input"
                   disabled={modeUnavailable}
                   onChange={(e) => setTarget(normalizeDecimal(e.target.value))}
                   placeholder={mode === "weight" ? "z.B. 1000" : "z.B. 10"} />
            <div className="flex items-center justify-center rounded-md border border-[var(--border)] bg-white font-mono text-xs text-[var(--text-muted)]">
              {targetUnit}
            </div>
          </div>
        </div>

        {/* Total batch + cost */}
        <div className="text-right">
          <div className="overline text-[10px]">Gesamtcharge</div>
          <div className="flex items-baseline justify-end gap-3">
            <div className="font-display font-black text-2xl text-[var(--pop-pink)]" data-testid="scale-total-weight">
              {fmtWeight(totalScaledG)}
            </div>
            <div className="text-right border-l border-[var(--pop-pink)]/25 pl-3" data-testid="scale-total-cost-block">
              <div className="overline text-[10px]">Kosten</div>
              <div className="font-display font-black text-2xl text-[var(--pop-pink)]" data-testid="scale-total-cost">
                {baseCostEur > 0
                  ? `${fmtDe(baseCostEur * factor, 2)} €`
                  : <span className="text-[var(--text-muted)] text-base">— €</span>}
              </div>
              {baseCostEur > 0 && targetNum > 0 && (
                <div className="text-[10px] text-[var(--text-muted)] font-mono" data-testid="scale-cost-per-unit">
                  {mode === "weight"
                    ? `${fmtDe((baseCostEur * factor) / (targetNum / 1000), 2)} €/kg`
                    : `${fmtDe((baseCostEur * factor) / targetNum, 2)} €/Stk`}
                </div>
              )}
            </div>
          </div>
          <div className="text-[10px] text-[var(--text-muted)] font-mono">
            Faktor ×{factor > 0 ? fmtDe(factor, 3) : "—"}
          </div>
        </div>
      </div>

      {modeUnavailable && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 flex items-center gap-2">
          <AlertTriangle size={12} />
          {mode === "weight"
            ? "Rezept hat keine gewichtbaren Zutaten (nur Stück). Bitte auf Nach Stück wechseln."
            : "Rezept hat keine Basis-Stückzahl. Bitte auf Nach Gewicht wechseln."}
        </div>
      )}

      {/* Scaled ingredients table */}
      <div className="rounded-xl border border-[var(--border)] bg-white overflow-hidden mb-3" data-testid="scaled-ingredients">
        <div className="bg-[var(--pop-cream)]/40 px-3 py-2 overline text-[10px] flex items-center justify-between">
          <span>Zutaten (skaliert)</span>
          <span className="text-[var(--text-muted)] font-mono normal-case">
            × {factor > 0 ? fmtDe(factor, 3) : "—"}
          </span>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {scaled.map((s, i) => (
              <tr key={i} className="border-t border-[var(--border)]" data-testid={`scaled-row-${i}`}>
                <td className="px-3 py-2 text-left">
                  <div className="font-medium">{s.item_name}</div>
                  {s.item_sku && <div className="text-[10px] font-mono text-[var(--text-muted)]">{s.item_sku}</div>}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[var(--pop-pink)] whitespace-nowrap">
                  {fmtDe(s.quantity, 2)} {s.unit}
                </td>
              </tr>
            ))}
            {scaled.length === 0 && (
              <tr><td colSpan={2} className="p-4 text-center text-xs text-[var(--text-muted)]">Keine Zutaten hinterlegt.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Percentage share table */}
      <div className="rounded-xl border border-[var(--border)] bg-white overflow-hidden" data-testid="percent-shares">
        <div className="bg-[var(--pop-cream)]/40 px-3 py-2 overline text-[10px]">
          Zutaten (Anteil an Gesamtcharge)
        </div>
        <table className="w-full text-sm">
          <tbody>
            {scaled.map((s, i) => (
              <tr key={i} className="border-t border-[var(--border)]" data-testid={`percent-row-${i}`}>
                <td className="px-3 py-2 text-left font-medium">{s.item_name}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[var(--pop-pink)] whitespace-nowrap">
                  {s.weighable ? `${fmtDe(s.percent, 2)} %` : <span className="text-[var(--text-muted)]">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
